import { ColumnsForLargeScreen, ColumnsForSmallScreen, GetCardWidthAndHeightFromCardSize } from "./DashboardUtil";
import { IWidget, Layout } from "./Dashboard.types";

import type { CardComponentType } from "Core/Card/Card.types";
import { newGuid } from "Shared/Utils";

export type WidgetStoreEntity = {
    widgetRegistryId: string;
    configMetadata?: string;
    isCard?: boolean;
};

/**
 * Looks up a card's size and default config by registry id. Pass `getCardRegistryById` from Apps/Dashboard/Cards/CardRegistry.
 * The registry imports every app's cards, so this module takes it as a parameter instead of importing it; it is loaded at startup.
 */
export type CardMetadataLookup = (registryId: string) => Pick<CardComponentType<object>, "cardSize" | "defaultConfig"> | undefined;

export type WidgetsToAdd = {
    cards: WidgetStoreEntity[];
    getCardMetadata: CardMetadataLookup;
};

export const getWidgetsWithLayout = (
    cards: WidgetStoreEntity[],
    getCardMetadata: CardMetadataLookup,
    pointYSm: number = 0,
    pointYLg: number = 0,
    pointXLg: number = 0
): IWidget[] => {
    const widgets: IWidget[] = [];
    cards.forEach((widget) => {
        const key = newGuid();
        const metadata = getCardMetadata(widget.widgetRegistryId);
        if (metadata) {
            const size = GetCardWidthAndHeightFromCardSize(metadata.cardSize);
            pointYSm += size.height;
            if (pointXLg + size.width > ColumnsForLargeScreen) {
                pointYLg += size.height;
                pointXLg = 0;
            }

            let mergedConfig = metadata.defaultConfig ?? {};
            if (widget.configMetadata) {
                const updatedConfig: unknown = JSON.parse(widget.configMetadata);
                if (typeof updatedConfig === "object" && updatedConfig !== null) {
                    mergedConfig = { ...mergedConfig, ...updatedConfig }; // Merge non-customized settings with the default settings
                }
            }

            widgets.push({
                widgetRegistryId: widget.widgetRegistryId,
                viewWidgetKey: key,
                layouts: [
                    Layout.create({ breakpointName: "sm", key: key, x: 0, y: pointYSm, w: ColumnsForSmallScreen, h: size.height }),
                    Layout.create({ breakpointName: "lg", key: key, x: pointXLg, y: pointYLg, w: size.width, h: size.height })
                ],
                config: JSON.stringify(mergedConfig)
            });
            pointXLg += size.width;
        }
    });
    return widgets;
};
